import { client } from "@lib/api"
import { useState, useEffect, useCallback } from "react"
import { SupportedBank } from "./useSupportedBank"
import { AddBankParams, EditBankParams, GetBankingResponse, RedemptionOption } from "../types/redemption"
import * as Sentry from "@sentry/react"

const useManagePaymentBank = (fetchBanks: boolean = true) => {
    const [bankAsRedemptionOption, setBankAsRedemptionOption] = useState<RedemptionOption[]>([])
    const [supportedBanks, setSupportedBanks] = useState<SupportedBank[]>([])
    const [banks, setBanks] = useState<GetBankingResponse[]>([])
    const [localLoading, setLocalLoading] = useState(true)

    const getBankData = async () => {
        try {
            setLocalLoading(true)
            const response = await client.doGet('/wallet/bankings/payment')
            const supportedBanks: SupportedBank[] = await client.doGet('/v2/wallet/supported-banks')
            setSupportedBanks(supportedBanks)
            const bankAsRedemptionOption: RedemptionOption[] = response.map((bank: GetBankingResponse) => {
                return {
                    id: bank.bank_uuid,
                    name: bank.bank_account_name,
                    iconUrl: supportedBanks?.find(supportedBank => supportedBank.code === bank.bank_name)?.icon_url || '',
                    accountNumber: bank.bank_account,
                    provider: supportedBanks?.find(supportedBank => supportedBank.code === bank.bank_name)?.display_name || '',
                    nickname: bank.account_nickname,
                    isValid: bank.status === 'VALIDATED',
                }
            })

            setBankAsRedemptionOption(bankAsRedemptionOption)
            setBanks(response)
            return response
        } catch (error) {
            Sentry.captureException(error)
            throw error
        } finally {
            setLocalLoading(false)
        }
    }
    
    const getData = useCallback(() => {
        getBankData()
    }, [])
    
    useEffect(() => {
        if (!fetchBanks) return
        getData()
    }, [fetchBanks, getData])

    const addBankPayment = async ({
        account_nickname,
        asset = "idr",
        source = "PAYMENT",
        bank_name,
        bank_account,
    }: AddBankParams) => {
        try {
            return await client.doPost('/v2/wallet/banking', {
                account_nickname,
                asset,
                source,
                bank_name,
                bank_account,
            })
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const editBankPayment = async ({
        bank_uuid,
        account_nickname,
        asset = "idr",
        source = "PAYMENT",
        bank_name,
        bank_account,
    }: EditBankParams) => {
        try {
            return await client.doPost(`/v2/wallet/banking/${bank_uuid}/update`, {
                account_nickname,
                asset,
                bank_name,
                bank_account,
                source,
            })
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const validateBankPayment = async ({
        bank_uuid
    }: {
        bank_uuid: string
    }) => {
        try {
            return await client.doGet(`/v2/wallet/banking/${bank_uuid}/validation`)
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const prepareBankPayment = async (params: (AddBankParams & { bank_uuid?: string })) => {
        try {
            setLocalLoading(true)
            let response
            if (params.bank_uuid) {
                response = await editBankPayment(params as EditBankParams)
            } else {
                response = await addBankPayment(params as AddBankParams)
            }

            if (response.status === 'VALIDATED') {
                return response
            } else if (response.status === 'VALIDATION_REQUESTED') {
                const secondResponse = await validateBankPayment({
                    bank_uuid: response.bank_uuid
                })
                if (secondResponse.status === 'VALIDATED') {
                    return secondResponse
                } else if (secondResponse.status === 'VALIDATION_REQUESTED') {
                    throw secondResponse.status
                } else {
                    throw secondResponse.status
                }
            } else {
                throw response.status
            }
        } catch (error) {
            throw error
        } finally {
            setLocalLoading(false)
        }
    }
    
    const isLoading = localLoading;
    
    return {
        data: banks as GetBankingResponse[],
        isLoading,
        supportedBanks,
        bankAsRedemptionOption,
        prepareBankPayment
    }
}

export default useManagePaymentBank

